// High Valyrian text: Geologica at SHRP 70 (the only place SHRP is used). See .hv in base.css.
// Inside a <WordLinks> scope its words are links that open them in the margin (margin/WordLink.tsx);
// `link` forces that on or off for one element. Inside a control (a button, a link, a label…) the
// words stay plain text: checked once, before the first paint. An element that holds linked words
// is their line: one tab stop, ← / → between the words (margin/WordLink.tsx markWordLine).
// Inside a <SyllableScope> with "Tavut tekstissä" on, its plain-text words show their syllables
// (syllables/Syllables.tsx).
import { Children, useLayoutEffect, useRef, useState, type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import { linkHvChildren, markWordLine, NO_LINK_INSIDE, NoWordLinks, useWordLinks } from '../margin/WordLink';
import { SyllableWord, syllableText, useInlineSyllables } from '../syllables/Syllables';

/** Plain-text children with their words in syllables (elements stay as they are). */
function syllableChildren(children: ReactNode): ReactNode {
  let i = 0;
  return Children.map(children, (c) => (typeof c === 'string' ? <>{syllableText(c, `y${i++}`)}</> : c));
}
const renderSyllables = (word: string) => <SyllableWord word={word} />;

export interface HvProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  children: ReactNode;
  /** Word links on/off for this element (default: the surrounding scope; off outside one). */
  link?: boolean;
}

/** Wraps HV text so it gets the HV face and a language tag (assistive tech, hyphenation off). */
export function Hv({ as: Tag = 'span', className, children, link, ...rest }: HvProps) {
  const scope = useWordLinks();
  const on = link ?? scope;
  const ref = useRef<HTMLElement>(null);
  const [inControl, setInControl] = useState(false);
  const syl = useInlineSyllables();
  useLayoutEffect(() => {
    // a one-time read of where the words were rendered (a control keeps them plain)
    // oxlint-disable-next-line react/set-state-in-effect
    if (on && ref.current?.parentElement?.closest(NO_LINK_INSIDE)) setInControl(true);
  }, [on]);
  // the line's tab stop, after every render (its words may change); a DOM write, no re-render
  useLayoutEffect(() => {
    if (ref.current) markWordLine(ref.current, on && !inControl);
  });
  return (
    <Tag ref={ref} className={className ? `hv ${className}` : 'hv'} lang="art-x-hval" translate="no" {...rest}>
      {!on ? (
        syl ? (
          syllableChildren(children)
        ) : (
          children
        )
      ) : inControl ? (
        <NoWordLinks>{syl ? syllableChildren(children) : children}</NoWordLinks>
      ) : (
        linkHvChildren(children, syl ? renderSyllables : undefined)
      )}
    </Tag>
  );
}
